/**
 * Sayfa bazlı SEO/meta yönetimi. Helmet benzeri bir kütüphane olmadığı için
 * i18n/index.tsx'teki updateMetaTag desenini genelleştirir.
 *
 * I18nProvider dil değişiminde varsayılan landing meta'larını geri yazdığından,
 * bir sayfa setPageSeo çağırdığında provider'ın bu üzerine yazması engellenir
 * (sayfaUnmount'ta releasePageSeo ile bırakılır).
 */

let pageSeoDepth = 0

/** Bir sayfa özel SEO taşıyor mu? I18nProvider bunu kontrol eder. */
export function hasPageSeoOverride() {
  return pageSeoDepth > 0
}

export interface PageSeoInput {
  title: string
  description: string
  /** Sayfanın tam canonical URL'i (https://dotkesk.com/blog/...). */
  canonical: string
  ogType?: 'website' | 'article'
  /** Sosyal paylaşım kartında çıkacak mutlak görsel URL'i (SVG olmamalı). */
  ogImage?: string
  /** og:type=article için zaman ve etiket meta'ları. */
  article?: { publishedTime?: string | null; modifiedTime?: string | null; tags?: string[] }
  /** Dil alternatifleri: link[rel=alternate][hreflang] + og:locale:alternate üretir. */
  alternates?: Array<{ language: string; url: string }>
  /** Sayfaya özgü yapılandırılmış veri; sayfadan çıkınca kaldırılır. */
  jsonLd?: Record<string, unknown>[]
  locale?: string
  noIndex?: boolean
}

function upsertMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

const PAGE_JSON_LD_ID = 'page-jsonld'

/** Sayfa SEO'sunu uygular; dönen fonksiyon çağrılınca landing varsayılanlarına döner. */
export function setPageSeo(seo: PageSeoInput): () => void {
  pageSeoDepth += 1
  const previous = {
    title: document.title,
    lang: document.documentElement.lang,
    robots: document.querySelector<HTMLMetaElement>('meta[name="robots"]')?.content,
    canonical: document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href,
    description: document.querySelector<HTMLMetaElement>('meta[name="description"]')?.content,
    ogTitle: document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.content,
    ogDescription: document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.content,
    ogUrl: document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.content,
    ogImage: document.querySelector<HTMLMetaElement>('meta[property="og:image"]')?.content,
    ogType: document.querySelector<HTMLMetaElement>('meta[property="og:type"]')?.content,
    ogLocale: document.querySelector<HTMLMetaElement>('meta[property="og:locale"]')?.content,
    twitterTitle: document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.content,
    twitterDescription: document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.content,
    twitterImage: document.querySelector<HTMLMetaElement>('meta[name="twitter:image"]')?.content,
  }

  document.title = seo.title
  if (seo.locale) document.documentElement.lang = seo.locale.split('_')[0]!

  upsertMeta('meta[name="description"]', 'name', 'description', seo.description)
  upsertMeta('meta[property="og:title"]', 'property', 'og:title', seo.title)
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', seo.description)
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', seo.canonical)
  upsertMeta('meta[property="og:type"]', 'property', 'og:type', seo.ogType ?? 'website')
  if (seo.locale) upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', seo.locale)
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title)
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description)
  upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', seo.ogImage ? 'summary_large_image' : 'summary')
  if (seo.ogImage) {
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', seo.ogImage)
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', seo.ogImage)
  }
  if (seo.noIndex !== undefined) upsertMeta('meta[name="robots"]', 'name', 'robots',
    seo.noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large')

  const articleMetas: Array<[string, string]> = []
  if (seo.article?.publishedTime) articleMetas.push(['article:published_time', seo.article.publishedTime])
  if (seo.article?.modifiedTime) articleMetas.push(['article:modified_time', seo.article.modifiedTime])
  for (const tag of seo.article?.tags ?? []) articleMetas.push(['article:tag', tag])
  for (const [key, content] of articleMetas)
    upsertMeta(`meta[property="${key}"]`, 'property', key, content)

  // Dil alternatifleri: hreflang link'leri + og:locale:alternate meta'ları.
  const altMarker = 'data-page-alt'
  document.querySelectorAll(`link[rel="alternate"][${altMarker}]`).forEach(el => el.remove())
  document.querySelectorAll(`meta[property="og:locale:alternate"][${altMarker}]`).forEach(el => el.remove())
  for (const alt of seo.alternates ?? []) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = alt.language
    link.href = alt.url
    link.setAttribute(altMarker, '1')
    document.head.appendChild(link)
    const meta = document.createElement('meta')
    meta.setAttribute('property', 'og:locale:alternate')
    meta.content = alt.language === 'en' ? 'en_US' : alt.language === 'de' ? 'de_DE' : alt.language === 'fr' ? 'fr_FR' : 'tr_TR'
    meta.setAttribute(altMarker, '1')
    document.head.appendChild(meta)
  }

  let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!canonicalEl) {
    canonicalEl = document.createElement('link')
    canonicalEl.rel = 'canonical'
    document.head.appendChild(canonicalEl)
  }
  canonicalEl.href = seo.canonical

  if (seo.jsonLd?.length) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = PAGE_JSON_LD_ID
    script.textContent = JSON.stringify(seo.jsonLd.length === 1 ? seo.jsonLd[0] : seo.jsonLd)
    document.head.appendChild(script)
  }

  return () => {
    document.title = previous.title
    document.documentElement.lang = previous.lang
    document.querySelectorAll('link[rel="alternate"][data-page-alt]').forEach(el => el.remove())
    document.querySelectorAll('meta[property="og:locale:alternate"][data-page-alt]').forEach(el => el.remove())
    const restore = (selector: string, value: string | undefined | null) => {
      const el = document.querySelector<HTMLMetaElement>(selector)
      if (el && value != null) el.content = value
    }
    restore('meta[name="description"]', previous.description)
    restore('meta[property="og:title"]', previous.ogTitle)
    restore('meta[property="og:description"]', previous.ogDescription)
    restore('meta[property="og:url"]', previous.ogUrl)
    restore('meta[property="og:type"]', previous.ogType)
    restore('meta[property="og:locale"]', previous.ogLocale)
    restore('meta[property="og:image"]', previous.ogImage)
    restore('meta[name="twitter:title"]', previous.twitterTitle)
    restore('meta[name="twitter:description"]', previous.twitterDescription)
    restore('meta[name="twitter:image"]', previous.twitterImage)
    restore('meta[name="robots"]', previous.robots)
    if (previous.canonical) {
      const el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      if (el) el.href = previous.canonical
    }
    document.getElementById(PAGE_JSON_LD_ID)?.remove()
    pageSeoDepth = Math.max(0, pageSeoDepth - 1)
  }
}

/**
 * Kapak görselini paylaşım kartında kullanılacak mutlak URL'e çevirir.
 * - mutlak URL → olduğu gibi
 * - "/uploads/..." → API origin (görseller API'den statik servis edilir)
 * - diğer göreli yollar (ör. "/blog/cover.png") → landing origin
 *
 * Sosyal crawler'lar SVG render etmediği için SVG kapaklarda og:image için
 * markanın hazır PNG görselini döner.
 */
export function resolveCoverUrl(cover: string | null | undefined): string | undefined {
  if (!cover) return undefined
  try {
    if (/^https?:\/\//i.test(cover)) return cover
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
    const siteBase = siteOrigin()
    if (cover.startsWith('/uploads/') && apiBase) return `${apiBase}${cover}`
    if (cover.startsWith('/')) return `${siteBase}${cover}`
    return cover
  } catch {
    return undefined
  }
}

/** og:image için crawler-dostu kapak: SVG ise marka PNG'ine düşer. */
export function resolveOgImage(cover: string | null | undefined): string | undefined {
  const resolved = resolveCoverUrl(cover)
  if (!resolved) return `${siteOrigin()}/og-image.png`
  if (/\.svg(\?|$)/i.test(resolved)) return `${siteOrigin()}/og-image.png`
  return resolved
}

export function siteOrigin(): string {
  if (typeof window === 'undefined') return 'https://dotkesk.com'
  // Varsayılan: mevcut origin (dev'de localhost, prod'da dotkesk.com).
  const configured = import.meta.env.VITE_LANDING_URL as string | undefined
  const isProdHost = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  if (configured && isProdHost) return configured.replace(/\/$/, '')
  return window.location.origin
}
